// Linhas da tabela do DRE simulado, na "visão aberta" da DRE da empresa (como no protótipo):
// para cada raiz totalizadora, primeiro as contas filhas e depois a própria raiz como subtotal
// ("= Receita Líquida"); raízes que não são totalizadoras aparecem normalmente.
// Abaixo dos nós ficam as categorias vinculadas (nível mais baixo do simulador).
// Depois da estrutura, a seção "Categorias fora da DRE" (linhas com `outside: true`; a primeira, a
// própria seção, com `sectionStart: true`).

export const ROW_KIND = Object.freeze({ NODE: 'node', TOTAL: 'total', CATEGORY: 'category' });

export const nodeRowKey = (nodeId) => `N|${nodeId}`;
export const categoryRowKey = (nodeId, cod) => `C|${nodeId}|${cod}`;

export function buildRows(model) {
  const rows = [];

  const pushCategories = (node, depth, ancestors, extra = {}) => {
    node.directCats.forEach((cod) => {
      rows.push({
        key: categoryRowKey(node.id, cod),
        kind: ROW_KIND.CATEGORY,
        cod,
        nodeId: node.id,
        label: model.categories.get(cod)?.descricao || cod,
        depth,
        ancestors,
        hasChildren: false,
        editable: true,
        ...extra,
      });
    });
  };

  const pushSubtree = (nodeId, depth, ancestors, extra = {}) => {
    const node = model.nodeById.get(nodeId);
    const key = nodeRowKey(nodeId);
    const hasChildren = node.childIds.length > 0 || node.directCats.length > 0;
    rows.push({
      key,
      kind: node.isTotalizador ? ROW_KIND.TOTAL : ROW_KIND.NODE,
      nodeId,
      label: node.descricao,
      depth,
      ancestors,
      hasChildren,
      editable: !node.isTotalizador,
      ...extra,
    });
    const childAncestors = [...ancestors, key];
    node.childIds.forEach((childId) => pushSubtree(childId, depth + 1, childAncestors, extra));
    pushCategories(node, depth + 1, childAncestors, extra);
  };

  model.rootIds.forEach((rootId) => {
    const root = model.nodeById.get(rootId);
    if (root.isTotalizador) {
      root.childIds.forEach((childId) => pushSubtree(childId, 0, []));
      pushCategories(root, 0, []);
      rows.push({
        key: nodeRowKey(rootId),
        kind: ROW_KIND.TOTAL,
        nodeId: rootId,
        label: root.descricao,
        depth: 0,
        ancestors: [],
        hasChildren: false,
        editable: false,
        isRootTotal: true,
      });
    } else {
      pushSubtree(rootId, 0, []);
    }
  });

  if (model.outsideRootId) {
    const start = rows.length;
    pushSubtree(model.outsideRootId, 0, [], { outside: true });
    rows[start] = { ...rows[start], sectionStart: true };
  }

  return rows;
}

/** Filtra linhas visíveis conforme os nós expandidos (todos os ancestrais precisam estar abertos). */
export const visibleRows = (rows, expandedKeys) =>
  rows.filter((row) => row.ancestors.every((key) => expandedKeys.has(key)));
